// Single source of truth for theme tokens.
//
// Colors are stored as hex for readability. cssVars.ts converts them to RGB
// triplet form ("250 250 250") and writes them to :root, where Tailwind
// classes (bg-bg, text-text, border-border, etc.) and antd's ConfigProvider
// both consume them.
//
// To tweak the design, edit values here. Don't add literal colors anywhere
// else in the codebase.

export type ThemeMode = 'light' | 'dark';

export interface TokenSet {
  color: {
    bg: string;
    bgElevated: string;
    panel: string;
    border: string;
    borderStrong: string;
    text: string;
    text2: string;
    text3: string;
    accent: string;
    accentSoft: string;
    success: string;
    successSoft: string;
    warn: string;
    warnSoft: string;
    danger: string;
    dangerSoft: string;
  };
  font: {
    sans: string;
    mono: string;
  };
  radius: {
    sm: number;
    md: number;
    lg: number;
  };
}

const fontStack = {
  sans: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
  mono: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
};

const radius = { sm: 4, md: 6, lg: 8 };

export const lightTokens: TokenSet = {
  color: {
    bg: '#fafafa',
    bgElevated: '#f4f4f5',
    panel: '#ffffff',
    border: '#ececef',
    borderStrong: '#d4d4d8',
    text: '#18181b',
    text2: '#52525b',
    text3: '#a1a1aa',
    accent: '#5b5bd6',
    accentSoft: '#ecebfd',
    success: '#16a34a',
    successSoft: '#dcfce7',
    warn: '#d97706',
    warnSoft: '#fef3c7',
    danger: '#dc2626',
    dangerSoft: '#fee2e2',
  },
  font: fontStack,
  radius,
};

// Linear/Vercel-style dark: soft panels on near-black bg, not pure #000.
export const darkTokens: TokenSet = {
  color: {
    bg: '#0a0a0c',
    bgElevated: '#161618',
    panel: '#0e0e10',
    border: '#1f1f23',
    borderStrong: '#2e2e33',
    text: '#ededed',
    text2: '#a1a1a6',
    text3: '#71717a',
    accent: '#7c7af0',
    accentSoft: '#1e1d3a',
    success: '#4ade80',
    successSoft: '#0f2a1a',
    warn: '#fbbf24',
    warnSoft: '#2e1f06',
    danger: '#f87171',
    dangerSoft: '#2e0f0f',
  },
  font: fontStack,
  radius,
};

export const tokenSets: Record<ThemeMode, TokenSet> = {
  light: lightTokens,
  dark: darkTokens,
};
